import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TaskIcon from '@mui/icons-material/Task';
import {
  Checkbox,
  Box,
  Button,
  Chip,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  InputAdornment,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  NativeSelect,
  CircularProgress,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import PrintTwoToneIcon from "@mui/icons-material/PrintTwoTone";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import TableSortLabel from "@mui/material/TableSortLabel";
import { useSelector } from "react-redux";
import SearchBox from "../../../Search/SearchBox";
import configServ from "../../../../services/config";
import getUploadDocumentUrl from "../../../../utility/getUploadDocumentUrl";
import CheckAndReturn from "../../../../utility/CheckAndReturn";
import { drawPurchaseOrderToPdf } from "../../../../utility/purchaseOrderPdf";
import useDebounce from "../../../hooks/useDebounce";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IconButton from "@mui/material/IconButton";
import PurchaseOrderItemTable from "./PurchaseOrderItemTable";
import PurchaseOrderWorkflowPopup from "./PurchaseOrderWorkflowPopup";
import PurchaseOrderOrientation from "./PurchaseOrderOrientation";
import { useNavigate } from "react-router-dom";
import LoaderNew from "../../../Loader/LoaderNew";
// import getUploadDocumentUrl from "../../../../utility/getUploadDocumentUrl";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import CustomDateRangePicker from "../../../DatePicker/CustomDateRangePicker";
import pdfImage from "../../../Assets/images/pdf.jfif";
import excelImage from "../../../Assets/images/excel.png";

export default function PurchaseOrderTable({ handleEditData, isChanged }) {
  //#region code
  const navigate = useNavigate();
  const { user_id, company_id, user_name, is_super, is_admin, permissions } = useSelector(
    (state) => state.GlobalVariables
  );
  const [page, setPage] = React.useState(0);
  const rowsPerPageOptions = [20, 50, 100, 200];
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [purchaseOrderList, setPurchaseOrderList] = useState([]);
  const [searchBy, setSearchBy] = useState("supplier");
  const [search, setSearch] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sessionYear, setSessionYear] = useState("2026-27");
  const [deliveryDateRange, setDeliveryDateRange] = useState([null, null]);
  const debounceSearch = useDebounce(search, 1000);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [purchaseOrderItemsCache, setPurchaseOrderItemsCache] = useState({});
  const [rowItemsLoading, setRowItemsLoading] = useState(null);
  const [workflowModal, setWorkflowModal] = useState({ open: false, row: null });
  const [companyDetails, setCompanyDetails] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [printModal, setPrintModal] = useState({ open: false, row: null });
  const [documentModal, setDocumentModal] = useState({ open: false, documents: [], selectedDoc: null });

  useEffect(() => {
    fetchPurchaseOrderList();
  }, [isChanged, page, rowsPerPage]);

  useEffect(() => {
    if (page === 0) {
      fetchPurchaseOrderList();
    } else {
      setPage(0);
    }
  }, [debounceSearch, statusFilter, sessionYear, deliveryDateRange]);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const res = await configServ.getCompanyList({ user_id });
        if (res.status === 200 && Array.isArray(res.data) && res.data.length > 0) {
          const selectedCompany = res.data.find((item) => item.id === company_id) || res.data[0];
          setCompanyDetails(selectedCompany);
        }
      } catch (e) {
        console.log('Failed to fetch company details for PDF export', e);
      }
    };
    if (user_id) {
      fetchCompanyDetails();
    }
  }, [user_id, company_id]);

  const fetchPurchaseOrderList = async () => {
    try {
      setLoading(true);
      const dataToSend = {
        user_id: user_id,
        company_id: company_id,
        page: page,
        per_page: rowsPerPage,
        search_by: searchBy,
        search: debounceSearch,
        status: statusFilter,
        session_year: sessionYear,
        delivery_start_date: deliveryDateRange[0]
          ? dayjs(deliveryDateRange[0]).format("YYYY-MM-DD")
          : "",
        delivery_end_date: deliveryDateRange[1]
          ? dayjs(deliveryDateRange[1]).format("YYYY-MM-DD")
          : "",
      };
      const res = await configServ.purchaseOrderList(dataToSend);
      if (res.status === 200 && Array.isArray(res.data)) {
        const result = res.data.map((prev) => ({
          ...prev,
          item: Array.isArray(prev.item)
            ? prev.item.map((prevs) => ({
                ...prevs,
                unit: prevs.product ? prevs.product.unit : null,
              }))
            : [],
        }));
        setPurchaseOrderList(result);
        setCount(res.total ?? result.length);
      } else {
        setPurchaseOrderList([]);
        setCount(0);
        if (res?.status && res.status !== 200) {
          console.error('Failed to fetch purchase orders:', res.message || res.error);
        }
      }
    } catch (e) {
      console.error('Failed to fetch purchase orders:', e);
      setPurchaseOrderList([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getUserIdByPo = async (poId) => {
    try {
      const res = await configServ.getUserIdByPo({ po_id: poId });
      console.log('getUserIdByPo response:', res);
      if (res.status === 200) {
        return res.data;
      }
    } catch (e) {
      console.log('Error fetching user ID by PO:', e);
    }
  };

  const headers = [
    "SUPPLIER",
    "ORDER NO",
    "TOTAL AMOUNT",
    "DELIVERY DATE",
    "STATUS",
    "ACTION",
  ];

  const headerCellStyle = {
    fontSize: 14, // Adjust the font size as needed
    fontWeight: "bold",
    backgroundColor: "#4CB5F5", // Set the header background color to blue
    color: "white",
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSearchBy = (event) => {
    try {
      setSearchBy(event.target.value);
    } catch (e) {
      console.log(e);
    }
  };

  const handleEdit = (params) => {
    handleEditData(params);
  };

  const evenRowStyle = {
    background: "#f5f5f5",
  };

  const oddRowStyle = {
    background: "white",
  };

  const rowStyle = {
    "&:hover": {
      background: "#f1f9fe",
    },
  };

  const toggleRowDetails = async (row) => {
    if (expandedRowId === row.id) {
      setExpandedRowId(null);
      return;
    }

    if (!purchaseOrderItemsCache[row.id]) {
      setRowItemsLoading(row.id);
      try {
        const res = await configServ.getPurchaseOrderItems({ id: row.id, company_id });
        if (res.status === 200) {
          setPurchaseOrderItemsCache((prev) => ({ ...prev, [row.id]: res.data }));
        } else {
          setPurchaseOrderItemsCache((prev) => ({ ...prev, [row.id]: [] }));
        }
      } catch (e) {
        console.log(e);
        setPurchaseOrderItemsCache((prev) => ({ ...prev, [row.id]: [] }));
      } finally {
        setRowItemsLoading(null);
      }
    }
    setExpandedRowId(row.id);
  };

  const handleOpenWorkflow = (row) => {
    setWorkflowModal({ open: true, row });
  };

  const handleCloseWorkflow = () => {
    setWorkflowModal({ open: false, row: null });
  };

  const handleWorkflowSaved = () => {
    fetchPurchaseOrderList();
  };

  const handleViewDocuments = async (row) => {
    try {
      const res = await configServ.getPurchaseOrderWorkflowDocuments({ po_id: row.id });
      const documents = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
      if (res.status === 200) {
        setDocumentModal({ open: true, documents, selectedDoc: null });
      } else {
        setDocumentModal({ open: true, documents: [], selectedDoc: null });
      }
    } catch (e) {
      console.log('Error fetching documents:', e);
      setDocumentModal({ open: true, documents: [], selectedDoc: null });
    }
  };

  const handleCloseDocumentModal = () => {
    setDocumentModal({ open: false, documents: [], selectedDoc: null });
  };

  const getDocumentUrl = getUploadDocumentUrl;

  const isImageDocument = (documentPath) => {
    return !!documentPath && /\.(jpe?g|png|gif|bmp|webp)$/i.test(documentPath);
  };

  const isPdfDocument = (documentPath) => {
    return !!documentPath && /\.pdf$/i.test(documentPath);
  };

  const getWorkflowStageText = (stage, availableUserName, loggedInUserName) => {
    let stageLabel = 'Stage';
    if (stage === 1 || stage === '1') stageLabel = 'Rechecked';
    else if (stage === 2 || stage === '2') stageLabel = 'Verified';
    else if (stage === 3 || stage === '3') stageLabel = 'Approved';
    else if (stage === 4 || stage === '4' || stage === 5 || stage === '5') stageLabel = 'Rejected';
    const userLabel = availableUserName || loggedInUserName || 'logged in user';
    return `${stageLabel} by ${userLabel}`;
  };

  const handleViewDocument = (documentPath) => {
    const url = getDocumentUrl(documentPath);
    if (!url) return;
    window.open(url, '_blank');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortedList = () => {
    if (!sortField) return purchaseOrderList;

    const sorted = [...purchaseOrderList].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'supplier') {
        aVal = a.supplier ? a.supplier.fname : '';
        bVal = b.supplier ? b.supplier.fname : '';
      }

      if (sortField === 'total_amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'delivery_date') {
        aVal = new Date(aVal).getTime() || 0;
        bVal = new Date(bVal).getTime() || 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getStatusLabel = (stage) => {
    if (stage === null || stage === undefined || stage === 0 || stage === '0') return 'New';
    if (stage === 1 || stage === '1') return 'Recheck';
    if (stage === 2 || stage === '2') return 'Verify';
    if (stage === 3 || stage === '3') return 'Approve';
    if (stage === 4 || stage === '4' || stage === 5 || stage === '5') return 'Rejected';
    return 'Unknown';
  };

  const handleSupplierClick = (params) => {
    try {
      const data = params;
      navigate("/account-master-entry/supplier", { state: data });
    } catch (e) {
      console.log(e);
    }
  };

  const handleExportPDF = async () => {
    try {
      const list = getSortedList();
      if (!list.length) {
        alert('No purchase orders to export.');
        return;
      }
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      // use for-loop so we can await fetching stage user per row
      for (let idx = 0; idx < list.length; idx++) {
        const row = list[idx];
        const items = purchaseOrderItemsCache[row.id] || row.item || [];
        // fetch workflow signatory data for this PO so PDF drawing has actual names
        let stageUser = {};
        try {
          const res = await configServ.getUserIdByPo({ po_id: row.id });
          if (res && res.status === 200) stageUser = res.data || {};
        } catch (e) {
          console.error('Failed to fetch stageUser for PO', row.id, e);
        }

        drawPurchaseOrderToPdf(doc, {
          row,
          items,
          copyCount: 1,
          stageUser,
          companyDetails,
        });
      }

      doc.save('purchase_orders.pdf');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF. Please try again.');
    }
  };

  const handleExportRowPDF = async (row) => {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const items = purchaseOrderItemsCache[row.id] || row.item || [];

      let stageUser = {};
      try {
        const res = await configServ.getUserIdByPo({ po_id: row.id });
        if (res && res.status === 200) stageUser = res.data || {};
      } catch (e) {
        console.error('Failed to fetch stageUser for PO', row.id, e);
      }

      drawPurchaseOrderToPdf(doc, {
        row,
        items,
        copyCount: 1,
        stageUser,
        companyDetails,
      });

      const fileName = `purchase_order_${(row.purchase_order_no || row.id).toString().replace(/[^a-zA-Z0-9-_]/g, '_')}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error exporting row PDF:', error);
      alert('Error generating PDF for this order. Please try again.');
    }
  };

  const handleExportExcel = async () => {
    try {
      const list = getSortedList();
      if (!list.length) {
        alert('No purchase orders to export.');
        return;
      }

      const excelData = list.map((row, index) => ({
        'S.No.': index + 1,
        'Supplier': row.supplier ? row.supplier.fname || row.supplier.supplier_name || row.supplier.name || '' : '',
        'Purchase Order No': row.purchase_order_no || '',
        'Total Amount': row.total_amount || 0,
        'Delivery Date': CheckAndReturn.transformToDate2(row.delivery_date),
        'Status': getStatusLabel(row.latest_workflow_stage),
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const columnWidths = [8, 25, 25, 20, 18, 18];
      worksheet['!cols'] = columnWidths.map((width) => ({ wch: width }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Purchase Orders');
      XLSX.writeFile(workbook, 'purchase_orders.xlsx');
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Error exporting Excel. Please try again.');
    }
  };

  const handlePrintPurchaseOrder = (row) => {
    const dataWithItems = {
      ...row,
      supplier_id: row.supplier?.id || row.supplier_id,
      items: purchaseOrderItemsCache[row.id] || row.item || [],
    };
    setPrintModal({ open: true, row: dataWithItems });
  };

  const handleClosePrintModal = () => {
    setPrintModal({ open: false, row: null });
  };

  //#endregion

  return (
    <>
      <Paper sx={{ width: "100%" }}>
        <Grid
          container
          alignItems="flex-end"
          justifyContent="flex-start"
          style={{ padding: 10, paddingTop: 0 }}
          spacing={1}
          direction={"row"}
        >
          <Grid item xs={12} justifyContent={"flex-start"}>
            <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
              Purchase Orders: {count}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={6} md={3} lg={2} xl={2} xxl={2}>
                <FormControl fullWidth>
                  <InputLabel variant="standard" htmlFor="search-by-native">
                    Search By
                  </InputLabel>
                  <NativeSelect
                    value={searchBy}
                    onChange={handleSearchBy}
                    inputProps={{
                      name: "searchBy",
                      id: "search-by-native",
                    }}
                  >
                    <option value={"supplier"}>Supplier</option>
                    <option value={"purchase_order_no"}>Purchase Order No</option>
                  </NativeSelect>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={2} xl={2} xxl={2}>
                <SearchBox search={search} setSearch={setSearch} sx={{ borderBottom: '1px solid #ccc', borderTop: 'none', borderLeft: 'none', borderRight: 'none' }} />
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={2} xl={2} xxl={2}>
                <FormControl fullWidth>
                  <InputLabel variant="standard" htmlFor="status-filter" shrink>
                    Status
                  </InputLabel>
                  <NativeSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    inputProps={{
                      name: "status",
                      id: "status-filter",
                    }}
                  >
                    <option value="">All</option>
                    <option value="0">New</option>
                    <option value="1">Recheck</option>
                    <option value="2">Verify</option>
                    <option value="3">Approve</option>
                    <option value="5">Rejected</option>
                  </NativeSelect>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={2} xl={2} xxl={2}>
                <FormControl fullWidth>
                  <InputLabel variant="standard" htmlFor="session-year-filter" shrink>
                    Session Year
                  </InputLabel>
                  <NativeSelect
                    value={sessionYear}
                    onChange={(e) => setSessionYear(e.target.value)}
                    inputProps={{
                      name: "sessionYear",
                      id: "session-year-filter",
                    }}
                  >
                    <option value="2023-24">2023-24</option>
                    <option value="2024-25">2024-25</option>
                    <option value="2025-26">2025-26</option>
                    <option value="2026-27">2026-27</option>
                    <option value="2027-28">2027-28</option>
                  </NativeSelect>
                </FormControl>
              </Grid>

              <Grid item xs={6} sm={6} md={6} lg={2} xl={2} xxl={2}>
                <CustomDateRangePicker
                  value={deliveryDateRange}
                  onChange={setDeliveryDateRange}
                />
              </Grid>

              <Grid item xs={6} sm={6} md={6} lg={2} xl={2} xxl={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                  <Tooltip title="Export to PDF">
                    <IconButton onClick={handleExportPDF}>
                      <img
                        src={pdfImage}
                        alt="pdf"
                        style={{ width: 28, height: 28 }}
                      />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Export to Excel">
                    <IconButton onClick={handleExportExcel}>
                      <img
                        src={excelImage}
                        alt="excel"
                        style={{ width: 28, height: 28 }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Active Filters */}
        {(statusFilter || sessionYear || (deliveryDateRange[0] && deliveryDateRange[1])) && (
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Active Filters:</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {statusFilter && (
                <Chip
                  label={`Status: ${getStatusLabel(statusFilter)}`}
                  onDelete={() => setStatusFilter("")}
                  color="primary"
                  variant="outlined"
                />
              )}
              {sessionYear && (
                <Chip
                  label={`Session Year: ${sessionYear}`}
                  onDelete={() => setSessionYear("2026-27")}
                  color="primary"
                  variant="outlined"
                />
              )}
              {deliveryDateRange[0] && deliveryDateRange[1] && (
                <Chip
                  label={`Delivery Date: ${dayjs(deliveryDateRange[0]).format('DD/MM/YYYY')} - ${dayjs(deliveryDateRange[1]).format('DD/MM/YYYY')}`}
                  onDelete={() => setDeliveryDateRange([null, null])}
                  color="primary"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>
        )}

        {/* desktop view */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100vh", // Set full height
            display: { xs: "none", sm: "none", lg: "block" },
          }}
        >
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle} />
                <TableCell sx={headerCellStyle}>S.No.</TableCell>
                <TableCell style={headerCellStyle}>
                  <TableSortLabel
                    active={sortField === 'supplier'}
                    direction={sortField === 'supplier' ? sortOrder : 'asc'}
                    onClick={() => handleSort('supplier')}
                  >
                    SUPPLIER
                  </TableSortLabel>
                </TableCell>
                <TableCell style={headerCellStyle}>
                  <TableSortLabel
                    active={sortField === 'purchase_order_no'}
                    direction={sortField === 'purchase_order_no' ? sortOrder : 'asc'}
                    onClick={() => handleSort('purchase_order_no')}
                  >
                    ORDER NO
                  </TableSortLabel>
                </TableCell>
                <TableCell style={headerCellStyle}>
                  <TableSortLabel
                    active={sortField === 'total_amount'}
                    direction={sortField === 'total_amount' ? sortOrder : 'asc'}
                    onClick={() => handleSort('total_amount')}
                  >
                    TOTAL AMOUNT
                  </TableSortLabel>
                </TableCell>
                <TableCell style={headerCellStyle}>
                  <TableSortLabel
                    active={sortField === 'delivery_date'}
                    direction={sortField === 'delivery_date' ? sortOrder : 'asc'}
                    onClick={() => handleSort('delivery_date')}
                  >
                    DELIVERY DATE
                  </TableSortLabel>
                </TableCell>
                <TableCell style={headerCellStyle}>STATUS</TableCell>
                <TableCell style={headerCellStyle}>ACTION</TableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <>
                <TableRow>
                  <TableCell colSpan={12} sx={{ textAlign: "center" }}>
                    <Box>
                      <LoaderNew />
                    </Box>
                  </TableCell>
                </TableRow>
              </>
            ) : (
              <>
                <TableBody>
                  {getSortedList().map((row, index) => {
                    const shouldShowEdit = !row.latest_workflow_stage || row.latest_workflow_stage === 4 || row.latest_workflow_stage === "4" || row.latest_workflow_stage === 5 || row.latest_workflow_stage === "5";
                    return (
                      <React.Fragment key={row.id}>
                        <TableRow
                          sx={{
                            ...rowStyle,
                            ...(index % 2 === 0 ? evenRowStyle : oddRowStyle),
                          }}
                        >
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleRowDetails(row)}>
                              <ExpandMoreIcon
                                sx={{
                                  transform: expandedRowId === row.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: '200ms',
                                }}
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <span
                              className="linkStyle"
                              onClick={() => handleSupplierClick(row.supplier ? row.supplier.fname : "")}
                            >
                              {row.supplier ? row.supplier.fname : ""}
                            </span>
                          </TableCell>
                          <TableCell>{row.purchase_order_no}</TableCell>
                          <TableCell sx={{ color: "green" }}>₹{row.total_amount}</TableCell>
                          <TableCell>{CheckAndReturn.transformToDate2(row.delivery_date)}</TableCell>
                          <TableCell>{getStatusLabel(row.latest_workflow_stage)}</TableCell>
                          <TableCell>
                            <Stack direction={"row"} spacing={1} alignItems="center">
                              <Box display={shouldShowEdit ? 'block' : 'none'}>
                                <EditIcon
                                  color="primary"
                                  sx={{
                                    borderRadius: 10,
                                    width: "fit-content",
                                    transition: "100ms",
                                    cursor: "pointer",
                                    padding: 0,
                                    ":hover": { backgroundColor: "#99999955" },
                                  }}
                                  onClick={() => handleEdit(row)}
                                />
                              </Box>
                              <Tooltip title="Print">
                                <IconButton
                                  size="small"
                                  onClick={() => handlePrintPurchaseOrder(row)}
                                  sx={{
                                    padding: 0,
                                    width: 34,
                                    height: 34,
                                  }}
                                >
                                  <PrintTwoToneIcon />
                                </IconButton>
                              </Tooltip>
                              {row.latest_workflow_stage && String(row.latest_workflow_stage) !== '0' ? (
                                <Tooltip title="View Attached Documents">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewDocuments(row)}
                                    sx={{
                                      padding: 0,
                                      width: 34,
                                      height: 34,
                                      color: '#4caf50',
                                    }}
                                  >
                                    <AttachFileIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              {!row.latest_workflow_stage ?(
                              <Tooltip title="Recheck PO">
                                <TaskIcon
                                  size="small"
                                  onClick={() => handleOpenWorkflow(row)}
                                  sx={{ 
                                    color: '#bdbdbd',
                                    "&:hover": { 
                                      backgroundColor: row.latest_workflow_stage >= 1 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(189, 189, 189, 0.1)'
                                    }
                                  }}
                                >
                                  {/* {row.latest_workflow_stage >= 1 ? <AssignmentTurnedInIcon /> : <AssignmentIcon />} */}
                                  <AssignmentIcon />
                                </TaskIcon>
                              </Tooltip>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={8} sx={{ p: 0, border: 'none' }}>
                            <Collapse in={expandedRowId === row.id} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, backgroundColor: '#f8faff' }}>
                                {rowItemsLoading === row.id ? (
                                  <LoaderNew />
                                ) : purchaseOrderItemsCache[row.id] && purchaseOrderItemsCache[row.id].length > 0 ? (
                                  <PurchaseOrderItemTable purchaseOrderItems={purchaseOrderItemsCache[row.id]} />
                                ) : (
                                  <Typography>No item details available.</Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </>
            )}
          </Table>
          <TablePagination
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      </Paper>

      {/* mobile view */}

      <Grid
        container
        spacing={2}
        mt={3}
        sx={{ display: { xs: 'flex', lg: 'none' }, overflowX: 'hidden' }}
      >
        {loading ? (
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <LoaderNew />
            </Box>
          </Grid>
        ) : getSortedList().length > 0 ? (
          getSortedList().map((row, index) => (
            <Grid item xs={12} key={row.id}>
              <Accordion expanded={expandedRowId === row.id} onChange={() => toggleRowDetails(row)}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                        {row.purchase_order_no}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>Supplier: {row.supplier ? row.supplier.fname : ''}</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>Total Amount: ₹{row.total_amount}</Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>Delivery Date: {CheckAndReturn.transformToDate2(row.delivery_date)}</Typography>
                      <Tooltip title="Print">
                        <IconButton
                          size="small"
                          onClick={() => handlePrintPurchaseOrder(row)}
                          sx={{ padding: 0, width: 34, height: 34 }}
                        >
                          <PrintTwoToneIcon />
                        </IconButton>
                      </Tooltip>
                      {row.latest_workflow_stage && String(row.latest_workflow_stage) !== '0' ? (
                        <Tooltip title="View Attached Documents">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDocuments(row)}
                            sx={{ padding: 0, width: 34, height: 34, color: '#4caf50' }}
                          >
                            <AttachFileIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {!row.latest_workflow_stage?(
                      <Tooltip title='Recheck PO'>
                        <TaskIcon
                          size="small"
                          onClick={() => handleOpenWorkflow(row)}
                          sx={{
                            color: '#bdbdbd',
                            '&:hover': {
                              backgroundColor: row.latest_workflow_stage >= 1 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(189, 189, 189, 0.1)'
                            }
                          }}
                        >
                          {/* {row.latest_workflow_stage >= 1 ? <AssignmentTurnedInIcon /> : <AssignmentIcon />} */}
                          <AssignmentIcon />
                        </TaskIcon>
                      </Tooltip>
                      ):null}
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {rowItemsLoading === row.id ? (
                    <LoaderNew />
                  ) : purchaseOrderItemsCache[row.id] && purchaseOrderItemsCache[row.id].length > 0 ? (
                    <PurchaseOrderItemTable purchaseOrderItems={purchaseOrderItemsCache[row.id]} />
                  ) : (
                    <Typography sx={{ color: 'red' }}>No item details available.</Typography>
                  )}
                </AccordionDetails>
              </Accordion>
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Box sx={{ color: 'red', textAlign: 'center' }}>No data found.</Box>
          </Grid>
        )}
      </Grid>
      <PurchaseOrderWorkflowPopup
        open={workflowModal.open}
        onClose={handleCloseWorkflow}
        poId={workflowModal.row?.id}
        poRow={workflowModal.row}
        stage={1}
        allowReject={false}
        onSaved={handleWorkflowSaved}
      />
      <PurchaseOrderOrientation
        open={printModal.open}
        setOpen={handleClosePrintModal}
        data={printModal.row}
        companyDetails={companyDetails}
      />

      <Dialog open={documentModal.open} onClose={handleCloseDocumentModal} maxWidth="sm" fullWidth>
        <DialogTitle>Attached Documents</DialogTitle>
        <DialogContent>
          {documentModal.documents.length > 0 ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {documentModal.documents.map((doc, index) => (
                <Box key={index} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box>
                      <Typography variant="body2" fontWeight={700}>
                        {getWorkflowStageText(doc.stage, doc.user_name, user_name)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {doc.created_at ? dayjs(doc.created_at).format('DD-MM-YYYY HH:mm') : 'No timestamp'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {doc.document ? (
                        isPdfDocument(doc.document) ? (
                          <IconButton size="small" onClick={() => handleViewDocument(doc.document)}>
                            <PictureAsPdfIcon color="error" />
                          </IconButton>
                        ) : isImageDocument(doc.document) ? (
                          <IconButton size="small" onClick={() => handleViewDocument(doc.document)}>
                            <img
                              src={getDocumentUrl(doc.document)}
                              alt={doc.document.split('/').pop()}
                              style={{ maxWidth: '120px', maxHeight: '120px', objectFit: 'contain', borderRadius: 4, border: '1px solid #ddd' }}
                              onClick={() => handleViewDocument(doc.document)}
                            />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => handleViewDocument(doc.document)}>
                            <DescriptionIcon color="action" />
                          </IconButton>
                        )
                      ) : (
                      <Tooltip title="No document attached">
                        <IconButton size="small" disabled={!doc.document}>
                          <AttachFileIcon />
                        </IconButton>
                      </Tooltip>
                      )
                      }
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Remarks:</strong> {doc.remarks || doc.remark || 'No remarks provided.'}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>No documents found for this Purchase Order.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDocumentModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
